import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { AppHeader } from './AppHeader'
import { AppSidebar } from './AppSidebar'
import type { NavigationSection } from './navigation'

export function AppShell({
  admin = false,
  sections,
  notice,
}: {
  admin?: boolean
  sections: NavigationSection[]
  notice?: ReactNode
}) {
  const { pathname } = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const menuOpenRef = useRef(menuOpen)
  const previousPath = useRef<string | null>(null)
  const dismissMobileMenu = useCallback(() => {
    setMenuOpen(false)
    requestAnimationFrame(() => document.getElementById('app-menu-trigger')?.focus())
  }, [])

  useEffect(() => {
    menuOpenRef.current = menuOpen
  }, [menuOpen])

  useEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'
    return () => {
      window.history.scrollRestoration = previousScrollRestoration
    }
  }, [])

  useEffect(() => {
    if (previousPath.current === pathname) return
    previousPath.current = pathname
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (menuOpenRef.current) return
        if (document.scrollingElement) document.scrollingElement.scrollTop = 0
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
        document.getElementById('main-content')?.focus({ preventScroll: true })
      })
    })
  }, [pathname])

  const shellContext = admin ? 'admin' : 'alumni'

  return (
    <div
      className={`app-shell app-shell--${shellContext}${sidebarCollapsed ? ' has-collapsed-sidebar' : ''}`}
    >
      <AppSidebar
        admin={admin}
        collapsed={sidebarCollapsed}
        mobileOpen={menuOpen}
        sections={sections}
        onDismissMobile={dismissMobileMenu}
        onNavigate={() => setMenuOpen(false)}
        onToggleCollapsed={() => setSidebarCollapsed((collapsed) => !collapsed)}
      />
      {menuOpen ? (
        <button
          className="sidebar-scrim"
          type="button"
          aria-label="Tutup menu"
          onClick={dismissMobileMenu}
        />
      ) : null}
      <div className="app-workspace">
        <AppHeader
          admin={admin}
          mobileOpen={menuOpen}
          sections={sections}
          onOpenMobile={() => setMenuOpen(true)}
        />
        <main id="main-content" className="app-content" tabIndex={-1}>
          <div className="app-content__inner">
            {notice}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
